// src/services/firebase.js

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// ─── 컬렉션 경로 헬퍼 ───
const pensionRef = (uid) => `pensions/${uid}`;
const col = (uid, name) => collection(db, `${pensionRef(uid)}/${name}`);
const docRef = (uid, name, id) => doc(db, `${pensionRef(uid)}/${name}`, id);

// ─── Auth ───

export async function firebaseRegister({ email, password, pensionName, phone }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  const uid = cred.user.uid;
  // 유저 프로필 저장
  await setDoc(doc(db, 'users', uid), {
    email,
    pensionName,
    phone,
    createdAt: serverTimestamp(),
  });
  return { uid, email, pensionName, phone };
}

export async function firebaseLogin(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const uid = cred.user.uid;
  const snap = await getDoc(doc(db, 'users', uid));
  const profile = snap.exists() ? snap.data() : {};
  return { uid, email, pensionName: profile.pensionName || '', phone: profile.phone || '' };
}

export async function firebaseAnonymousLogin() {
  const cred = await signInAnonymously(auth);
  return { uid: cred.user.uid, email: null, pensionName: '체험 계정', phone: '', isAnonymous: true };
}

export async function firebaseLogout() {
  await signOut(auth);
}

export async function firebaseSendPasswordReset(email) {
  await sendPasswordResetEmail(auth, email);
}

export function onAuthChange(callback) {
  return onAuthStateChanged(auth, async (fbUser) => {
    if (!fbUser) { callback(null); return; }
    // 익명 로그인은 Firestore 프로필 없이 바로 진행
    if (fbUser.isAnonymous) {
      callback({ uid: fbUser.uid, email: null, pensionName: '체험 계정', phone: '', isAnonymous: true });
      return;
    }
    const snap = await getDoc(doc(db, 'users', fbUser.uid));
    const profile = snap.exists() ? snap.data() : {};
    callback({
      uid: fbUser.uid,
      email: fbUser.email,
      pensionName: profile.pensionName || '',
      phone: profile.phone || '',
      isAnonymous: false,
    });
  });
}

// ─── 객실 ───

export async function fsGetRooms(uid) {
  const snap = await getDocs(query(col(uid, 'rooms'), orderBy('createdAt', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fsAddRoom(uid, data) {
  const { id, ...fields } = data;
  if (id) {
    // 앱이 생성한 custom id를 Firestore 문서 경로로 사용 (삭제 시 경로 일치 보장)
    await setDoc(docRef(uid, 'rooms', id), { ...fields, createdAt: serverTimestamp() });
    return id;
  }
  // id 없으면 Firestore 자동 생성
  const ref = await addDoc(col(uid, 'rooms'), { ...fields, createdAt: serverTimestamp() });
  return ref.id;
}

export async function fsUpdateRoom(uid, roomId, data) {
  await updateDoc(docRef(uid, 'rooms', roomId), { ...data, updatedAt: serverTimestamp() });
}

export async function fsDeleteRoom(uid, roomId) {
  // 1. 직접 경로로 삭제 시도 (신규 방식: doc 경로 = roomId)
  const directRef = docRef(uid, 'rooms', roomId);
  const directSnap = await getDoc(directRef);
  if (directSnap.exists()) {
    await deleteDoc(directRef);
    return;
  }
  // 2. 구버전 방(addDoc으로 생성 → id가 data 필드에 저장된 경우) 폴백 처리
  const q = query(col(uid, 'rooms'), where('id', '==', roomId));
  const snap = await getDocs(q);
  if (!snap.empty) {
    await deleteDoc(snap.docs[0].ref);
    return;
  }
  throw new Error(`객실을 찾을 수 없습니다 (id: ${roomId})`);
}

export async function fsSetRooms(uid, rooms) {
  // 온보딩: 기존 방 모두 교체
  const existing = await getDocs(col(uid, 'rooms'));
  const deletes = existing.docs.map(d => deleteDoc(d.ref));
  await Promise.all(deletes);
  const adds = rooms.map(r => setDoc(docRef(uid, 'rooms', r.id), { ...r, createdAt: serverTimestamp() }));
  await Promise.all(adds);
}

export function onRoomsChange(uid, callback) {
  const q = query(col(uid, 'rooms'), orderBy('createdAt', 'asc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

// ─── 예약 ───

export async function fsAddReservation(uid, data) {
  // data.id가 있으면 그걸 Firestore 문서 ID로 사용 — update 시 ID 불일치 방지
  const ref = data.id ? docRef(uid, 'reservations', data.id) : doc(col(uid, 'reservations'));
  await setDoc(ref, { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function fsUpdateReservation(uid, resId, data) {
  await updateDoc(docRef(uid, 'reservations', resId), { ...data, updatedAt: serverTimestamp() });
}

export async function fsBatchUpdateReservations(uid, ids, data) {
  const batch = writeBatch(db);
  ids.forEach(id => batch.update(docRef(uid, 'reservations', id), { ...data, updatedAt: serverTimestamp() }));
  await batch.commit();
}

export function onReservationsChange(uid, callback) {
  const q = query(col(uid, 'reservations'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ ...d.data(), id: d.id }))));
}

// ─── 고객 ───

export async function fsAddCustomer(uid, data) {
  const ref = doc(col(uid, 'customers'));
  await setDoc(ref, { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function fsUpdateCustomer(uid, customerId, data) {
  await updateDoc(docRef(uid, 'customers', customerId), { ...data, updatedAt: serverTimestamp() });
}

export function onCustomersChange(uid, callback) {
  const q = query(col(uid, 'customers'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

// ─── 메시지 로그 ───

export async function fsLogMessage(uid, data) {
  await addDoc(col(uid, 'messages'), { ...data, sentAt: serverTimestamp() });
}

export function onMessagesChange(uid, callback) {
  const q = query(col(uid, 'messages'), orderBy('sentAt', 'desc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

// ─── 객실별 메시지 템플릿 ───

export async function fsGetRoomTemplates(uid) {
  const snap = await getDoc(doc(db, `${pensionRef(uid)}/settings/roomTemplates`));
  return snap.exists() ? snap.data() : {};
}

export async function fsSaveRoomTemplates(uid, templates) {
  await setDoc(doc(db, `${pensionRef(uid)}/settings/roomTemplates`), templates);
}

// ─── 공통 템플릿 (예약확인·입금확인·리뷰요청) ───

export async function fsGetCommonTemplates(uid) {
  const snap = await getDoc(doc(db, `${pensionRef(uid)}/settings/commonTemplates`));
  return snap.exists() ? snap.data() : {};
}

export async function fsSaveCommonTemplates(uid, templates) {
  await setDoc(doc(db, `${pensionRef(uid)}/settings/commonTemplates`), templates);
}

// ─── 리뷰 ───

function generateToken() {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export async function fsCreateReviewToken(uid, { customerId, customerName, customerPhone, pensionName }) {
  const token = generateToken();
  await setDoc(doc(db, 'review_tokens', token), {
    uid, customerId: customerId || null,
    customerName, customerPhone, pensionName,
    createdAt: serverTimestamp(), used: false,
  });
  return token;
}

export async function fsGetReviewContext(token) {
  const snap = await getDoc(doc(db, 'review_tokens', token));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function fsSubmitReview(token, { rating, comment, privateFeedback, marketingConsent }) {
  const ctx = await fsGetReviewContext(token);
  if (!ctx) throw new Error('Invalid token');
  const { uid, customerId, customerName, customerPhone } = ctx;

  await addDoc(col(uid, 'reviews'), {
    token, rating, comment: comment || '', privateFeedback: privateFeedback || '',
    marketingConsent, customerId: customerId || null,
    customerName, customerPhone,
    createdAt: serverTimestamp(), read: false,
  });

  if (marketingConsent) {
    if (customerId) {
      await updateDoc(docRef(uid, 'customers', customerId), {
        marketing_consent: true,
        marketing_consent_at: serverTimestamp(),
      });
    } else if (customerPhone) {
      const q = query(col(uid, 'customers'), where('phone', '==', customerPhone));
      const snap = await getDocs(q);
      if (!snap.empty) {
        await updateDoc(snap.docs[0].ref, {
          marketing_consent: true,
          marketing_consent_at: serverTimestamp(),
        });
      }
    }
  }

  await updateDoc(doc(db, 'review_tokens', token), { used: true });
}

export function onReviewsChange(uid, callback) {
  const q = query(col(uid, 'reviews'), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

export async function fsMarkReviewRead(uid, reviewId) {
  await updateDoc(docRef(uid, 'reviews', reviewId), { read: true });
}

// ─── 웰컴 토큰 ───

export async function fsCreateWelcomeToken(uid, { customerId, customerName, customerPhone, pensionName }) {
  const token = generateToken();
  await setDoc(doc(db, 'welcome_tokens', token), {
    uid, customerId: customerId || null,
    customerName, customerPhone, pensionName,
    createdAt: serverTimestamp(), used: false,
  });
  return token;
}

export async function fsGetWelcomeContext(token) {
  const snap = await getDoc(doc(db, 'welcome_tokens', token));
  if (!snap.exists()) return null;
  return snap.data();
}

export async function fsSubmitWelcome(token, { interests, marketingConsent }) {
  const ctx = await fsGetWelcomeContext(token);
  if (!ctx) throw new Error('Invalid token');
  const { uid, customerId } = ctx;

  if (customerId) {
    const updates = { interests: interests || [] };
    if (marketingConsent) {
      updates.marketing_consent = true;
      updates.marketing_consent_at = serverTimestamp();
    }
    await updateDoc(docRef(uid, 'customers', customerId), updates);
  }

  await updateDoc(doc(db, 'welcome_tokens', token), { used: true });
}

// ─── 파싱 로그 ───

export async function fsLogParsingEvent(uid, data) {
  await addDoc(col(uid, 'parsing_logs'), { ...data, timestamp: serverTimestamp() });
}

// 관리자 전용 — 전체 사용자의 parsing_logs를 collectionGroup으로 집계
export async function fsGetAdminParsingReport() {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const snap = await getDocs(
    query(collectionGroup(db, 'parsing_logs'), orderBy('timestamp', 'desc'), limit(1000))
  );
  const logs = snap.docs
    .map(d => d.data())
    .filter(d => d.timestamp?.toDate() >= thirtyDaysAgo);

  const total = logs.length;
  const empty = { total: 0, editedCount: 0, editRate: 0, fieldCounts: {}, otaStats: [], top3Fields: [], top3OTAs: [], updatedAt: new Date() };
  if (total === 0) return empty;

  const FIELDS = ['name', 'phone', 'checkin', 'checkout', 'guests', 'room'];
  const fieldCounts = Object.fromEntries(FIELDS.map(f => [f, 0]));
  const otaMap = {};
  let editedCount = 0;

  for (const log of logs) {
    const edited = log.editedFields || [];
    if (edited.length > 0) editedCount++;
    for (const field of edited) {
      if (fieldCounts[field] !== undefined) fieldCounts[field]++;
    }
    const ch = log.channel || '기타';
    if (!otaMap[ch]) otaMap[ch] = { total: 0, edited: 0 };
    otaMap[ch].total++;
    if (edited.length > 0) otaMap[ch].edited++;
  }

  const top3Fields = Object.entries(fieldCounts)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const otaStats = Object.entries(otaMap).map(([channel, s]) => ({
    channel,
    total: s.total,
    edited: s.edited,
    rate: Math.round((s.edited / s.total) * 100),
  }));

  const top3OTAs = [...otaStats].sort((a, b) => b.rate - a.rate).slice(0, 3);

  return {
    total,
    editedCount,
    editRate: Math.round((editedCount / total) * 100),
    fieldCounts,
    otaStats,
    top3Fields,
    top3OTAs,
    updatedAt: new Date(),
  };
}
